// AdminAuditsPage — pestaña "Auditorías" (salud de datos de Lealify).
// Muestra el último snapshot del motor (lealify-base: lib/dataAudit.js + audit_runs),
// el detalle por chequeo (semáforo + ejemplos) y el HISTORIAL (log) de corridas.
// Hoy solo corrida manual ("correr ahora"); el cron queda pendiente operativo.
// Diseño: docs/lealify-auditorias-panel-diseno.md

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import Layout from '../../../components/layout/Layout';
import {
  ShieldCheck, RefreshCw, Play, ChevronDown, ChevronUp, LifeBuoy,
  CheckCircle2, AlertTriangle, XCircle, Info, Building2, Users, Activity,
} from 'lucide-react';
import {
  adminAuditsApi,
  AuditSnapshot, AuditRunSummary, AuditStatus, AuditCheck,
} from '../../../api/adminAuditsApi';

const fmtDateTime = (ms: number | null) =>
  ms ? new Date(ms).toLocaleString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

const HEALTH = {
  ok:       { label: 'Todo en orden', cls: 'bg-green-100 dark:bg-green-500/20 text-green-800 dark:text-green-300', Icon: CheckCircle2 },
  warn:     { label: 'Atención',       cls: 'bg-amber-100 dark:bg-amber-500/20 text-amber-800 dark:text-amber-300', Icon: AlertTriangle },
  critical: { label: 'Crítico',        cls: 'bg-red-100 dark:bg-red-500/20 text-red-800 dark:text-red-300',         Icon: XCircle },
  error:    { label: 'Error de chequeo', cls: 'bg-gray-100 dark:bg-gray-500/20 text-gray-700 dark:text-gray-300',   Icon: Info },
} as const;

const StatusBadge: React.FC<{ status: AuditStatus; informational?: boolean }> = ({ status, informational }) => {
  const h = HEALTH[status] || HEALTH.error;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${h.cls}`}>
      <h.Icon className="h-3.5 w-3.5 mr-1" />
      {informational ? 'Informativo' : h.label}
    </span>
  );
};

const StatCard: React.FC<{ icon: React.ElementType; label: string; value: number | undefined }> = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3 bg-surface border border-line rounded-lg px-4 py-3">
    <Icon className="h-5 w-5 text-accent" />
    <div>
      <p className="text-lg font-bold text-content leading-none">{value ?? '—'}</p>
      <p className="text-xs text-content-2 mt-1">{label}</p>
    </div>
  </div>
);

const AdminAuditsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const [snapshot, setSnapshot] = useState<AuditSnapshot | null>(null);
  const [history, setHistory] = useState<AuditRunSummary[]>([]);
  const [viewingRunId, setViewingRunId] = useState<string | null>(null); // null = última
  const [expanded, setExpanded] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [latest, hist] = await Promise.all([adminAuditsApi.fetchLatest(), adminAuditsApi.fetchHistory(30)]);
      setSnapshot(latest);
      setHistory(hist);
      setViewingRunId(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error cargando auditorías.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setRunning(true);
    setError('');
    try {
      const snap = await adminAuditsApi.runNow();
      setSnapshot(snap);
      setViewingRunId(null);
      const hist = await adminAuditsApi.fetchHistory(30);
      setHistory(hist);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error corriendo la auditoría.');
    } finally {
      setRunning(false);
    }
  };

  const openRun = async (runId: string) => {
    if (runId === (snapshot?.runId || '')) return;
    setLoading(true);
    try {
      const r = await adminAuditsApi.fetchByRunId(runId);
      setSnapshot(r);
      setViewingRunId(runId);
      setExpanded('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error abriendo la corrida.');
    } finally {
      setLoading(false);
    }
  };

  // chequeos accionables primero (críticos arriba), informativos al final
  const checks = useMemo(() => {
    const rank: Record<string, number> = { critical: 0, error: 1, warn: 2, ok: 3 };
    return [...(snapshot?.checks || [])].sort((a, b) => {
      if (!!a.informational !== !!b.informational) return a.informational ? 1 : -1;
      return (rank[a.status] ?? 9) - (rank[b.status] ?? 9);
    });
  }, [snapshot]);

  if (currentUser?.role !== 'admin') {
    return (
      <Layout>
        <div className="bg-red-50 dark:bg-red-500/10 p-4 rounded-md shadow border border-red-200 animate-fade-in-up">
          <div className="flex">
            <LifeBuoy className="h-5 w-5 text-red-400 flex-shrink-0" aria-hidden="true" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-300">Acceso restringido</h3>
              <p className="mt-2 text-sm text-red-700 dark:text-red-300">Solo los administradores pueden ver el panel de auditorías.</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const globalHealth = snapshot ? (HEALTH[snapshot.health] || HEALTH.error) : null;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 animate-fade-in-up">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-content flex items-center">
              <ShieldCheck className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-accent" />
              Auditorías de datos
            </h1>
            <p className="text-sm text-content-2 mt-1">Salud de los datos de Lealify. Se recalcula 1×/día; aquí ves la última corrida y el historial.</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              disabled={loading || running}
              className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md border border-line bg-surface text-content hover:bg-surface-2 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
              Refrescar
            </button>
            <button
              onClick={runNow}
              disabled={running || loading}
              className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md bg-accent-solid text-white hover:bg-accent-solid/90 disabled:opacity-50"
            >
              <Play className={`h-4 w-4 mr-1.5 ${running ? 'animate-pulse' : ''}`} />
              {running ? 'Corriendo…' : 'Correr ahora'}
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 text-red-700 dark:text-red-300 text-sm rounded-md px-4 py-3">{error}</div>
        )}

        {loading && !snapshot ? (
          <p className="text-sm text-content-3 animate-pulse">Cargando auditoría…</p>
        ) : !snapshot ? (
          <p className="text-sm text-content-2">Aún no hay corridas. Pulsa «Correr ahora» para generar la primera.</p>
        ) : (
          <>
            {/* Banner de última corrida */}
            <div className="flex flex-wrap items-center gap-3 bg-surface border border-line rounded-lg px-4 py-3 animate-fade-in-up">
              {globalHealth && (
                <span className={`inline-flex items-center px-3 py-1 text-sm font-semibold rounded-full ${globalHealth.cls}`}>
                  <globalHealth.Icon className="h-4 w-4 mr-1.5" />
                  {globalHealth.label}
                </span>
              )}
              <span className="text-sm text-content-2">
                {viewingRunId ? 'Corrida' : 'Última corrida'}: <span className="text-content font-medium">{fmtDateTime(snapshot.ranAt)}</span>
                {' · '}{snapshot.trigger === 'manual' ? 'manual' : 'automática'}
                {' · '}{(snapshot.durationMs / 1000).toFixed(1)}s
              </span>
              {viewingRunId && (
                <button onClick={load} className="text-sm text-accent hover:underline">← volver a la última</button>
              )}
            </div>

            {/* Resumen */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 animate-fade-in-up">
              <StatCard icon={Building2} label="Negocios" value={snapshot.totals?.businesses} />
              <StatCard icon={Users} label="Dueños" value={snapshot.totals?.users} />
              <StatCard icon={Activity} label="Negocios activos" value={snapshot.totals?.active30d} />
            </div>

            {/* Chequeos */}
            <div className="bg-surface border border-line rounded-lg overflow-hidden animate-fade-in-up">
              {checks.map((c: AuditCheck) => {
                const open = expanded === c.key;
                return (
                  <div key={c.key} className="border-b border-line last:border-b-0">
                    <button
                      onClick={() => setExpanded(open ? '' : c.key)}
                      className="w-full flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 hover:bg-surface-2 text-left"
                    >
                      <StatusBadge status={c.status} informational={c.informational} />
                      <span className="font-medium text-content min-w-0">{c.label}</span>
                      <span
                        className="text-sm text-content-2 order-last basis-full truncate sm:order-none sm:basis-auto sm:flex-1 sm:min-w-0"
                        title={c.metric}
                      >
                        {c.metric}
                      </span>
                      <span className="ml-auto sm:ml-0 flex-shrink-0">
                        {((c.examples && c.examples.length) || c.hint)
                          ? (open ? <ChevronUp className="h-4 w-4 text-content-3" /> : <ChevronDown className="h-4 w-4 text-content-3" />)
                          : <span className="block w-4" />}
                      </span>
                    </button>
                    {open && (
                      <div className="px-4 pb-3 pt-1 space-y-2 bg-surface-2/40">
                        {(c.examples || []).map((e, i) => (
                          <div key={i} className="flex items-start gap-2 text-sm text-content-2">
                            <span className="text-content-3 mt-0.5">•</span>
                            <span>{e.label}{e.ref && <span className="text-content-3 ml-1">({e.ref})</span>}</span>
                          </div>
                        ))}
                        {!c.examples?.length && <p className="text-sm text-content-3">Sin elementos a listar.</p>}
                        {c.hint && <p className="text-xs text-content-3 mt-2 italic">💡 {c.hint}</p>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Historial (log) */}
            <div className="animate-fade-in-up">
              <h2 className="text-sm font-semibold text-content-2 mb-2">Historial</h2>
              <div className="flex flex-wrap gap-2">
                {history.map((r) => {
                  const h = HEALTH[r.health] || HEALTH.error;
                  const active = r.runId === snapshot.runId;
                  return (
                    <button
                      key={r.runId}
                      onClick={() => openRun(r.runId)}
                      title={`${fmtDateTime(r.ranAt)} · ${r.trigger}`}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-md border ${active ? 'border-accent' : 'border-line'} bg-surface hover:bg-surface-2`}
                    >
                      <h.Icon className={`h-3.5 w-3.5 ${r.health === 'ok' ? 'text-green-600 dark:text-green-400' : r.health === 'warn' ? 'text-amber-600 dark:text-amber-400' : r.health === 'critical' ? 'text-red-600 dark:text-red-400' : 'text-gray-500'}`} />
                      <span className="text-content-2">{fmtDateTime(r.ranAt)}</span>
                    </button>
                  );
                })}
                {!history.length && <span className="text-sm text-content-3">Sin corridas previas.</span>}
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default AdminAuditsPage;
